import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalizedV2,
  IExperienceMessageDenormalized,
  ITaskDenormalized,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import {
  PAEventType,
  ProductAnalyticsEvent
} from '@dadohr/shared/domainTypes/productAnalytics';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton, Popover, Typography } from '@material-ui/core';
import { capitalize, compact, findLast, partition, sortBy } from 'lodash';
import { Check, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ColoredChip } from '../../../../../../components/ColoredChip';
import { ExclamationMarkIcon } from '../../../../../../components/CustomIcons';
import { Spacer } from '../../../../../../components/Spacer';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { Timestamp, toMoment, toMomentFromAny } from '../../../../../../services/time';
import { WithBreakpoints } from '../Breakpoints';
import { EmployeeAppCard } from '../EmployeeAppCard';
import { NAV_BG_COLOR } from './common';

const WeekNavText = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w600
}));

export type InstanceWeek = {
  count: number;
  messages: IExperienceMessageDenormalized[];
  tasks: ITaskDenormalized[];
  anchor?: {
    at: Timestamp;
    label: string;
  };
};

export const getStartingWeek = (
  instance: Doc<IExperienceDenormalizedV2>,
  weekGroups: InstanceWeek[],
  n: moment.Moment
) => {
  const anchorTimestamp = instance.data.anchor?.at || instance.data.startsAt;
  const anchor = anchorTimestamp
    ? toMoment(anchorTimestamp)
    : toMomentFromAny(Date.now()); // Fallback to current time if no anchor timestamp
  const diff = n.diff(anchor, 'days');
  const week = Math.floor(diff / 7);
  const trueWeek = week < 0 ? week : week + 1;
  const withoutAnchorGroups = weekGroups.filter((w) => !w.anchor);

  // Either find where we are right now, or the PREVIOUS week (this initially used to be the NEXT week).
  // When we're past everything, default to the last one available.
  // When we're before everything, select the first one.
  // It's important that there is something selected, to avoid strange UI states.
  if (!withoutAnchorGroups.length) {
    // This should never happen
    // Be careful when removing this condition - as we rely on something being present in the code below
    return trueWeek;
  }
  const min = withoutAnchorGroups[0];
  const max = withoutAnchorGroups[withoutAnchorGroups.length - 1];
  const currentWeek =
    withoutAnchorGroups.find((w) => w.count === trueWeek) ||
    findLast(withoutAnchorGroups, (w) => w.count < trueWeek) ||
    (trueWeek < min.count ? min : undefined) ||
    (trueWeek > max.count ? max : undefined);

  if (max && max.count < trueWeek) {
    if (instance.data.tasks.some((tsk) => tsk.status === TaskStatus.LATE)) {
      return currentWeek?.count || trueWeek;
    } else {
      return trueWeek;
    }
  }
  return currentWeek?.count || trueWeek;
};

export const groupWeeks = (instance: Doc<IExperienceDenormalizedV2>) => {
  // For any distance calcaulations, we want to be "imprecise" by design.
  // - When a template starts at 9am, and a message sends at 7am, we still want
  //   it to be e.g. in Week 2, if it's in the second week.
  // - A naive day/week diffing would place it in Week 1
  // - There's also daylight savings time to consider, which can introduce another
  //   shift by a single day
  // - We therefore just compare actual days, by doing startOf('day')
  // - For this to work properly, we also respect the template's time zone
  // - It doesn't matter that other dates in the employee app are shown in the user's timezone
  // - We're interested in distances on a template level. Thus bringing everything to the same
  //   denominator (same TZ, same start of day) will lead to the correct results.
  const tz = instance.data.tplTz;
  const weeks: { [key: string]: InstanceWeek } = {};
  const anchorTimestamp = instance.data.anchor?.at || instance.data.startsAt;
  const anchor = anchorTimestamp
    ? toMoment(anchorTimestamp).tz(tz).startOf('d')
    : toMomentFromAny(Date.now()).tz(tz).startOf('d'); // Fallback to current time if no anchor timestamp
  instance.data.messages.forEach((m) => {
    if (!m.sendAt) return; // Skip messages without sendAt timestamp
    const at = toMoment(m.sendAt).tz(tz).startOf('day');
    const diff = Math.floor(at.diff(anchor, 'days'));
    const week = Math.floor(diff / 7);
    const trueWeek = week < 0 ? week : week + 1;
    const container = (weeks[trueWeek] = weeks[trueWeek] || {
      count: trueWeek,
      messages: [],
      tasks: []
    });
    container.messages.push(m);
  });

  instance.data.tasks.forEach((t) => {
    if (!t.start || !t.end) return; // Skip tasks without start/end timestamps
    const diffStart = toMoment(t.start)
      .tz(tz)
      .startOf('day')
      .diff(anchor, 'days');
    const weekStart = Math.floor(diffStart / 7); //  + (diffStart < 0 ? 0 : 1);
    const diffEnd =
      toMoment(t.end).tz(tz).startOf('day').diff(anchor, 'days') - 1; // ends are date exclusive, so we have to subtract one day to get the actual number
    const weekEnd = Math.floor(diffEnd / 7); // + (diffEnd < 0 ? 0 : 1);
    const trueWeekStart = weekStart < 0 ? weekStart : weekStart + 1;
    const trueWeekEnd = weekEnd < 0 ? weekEnd : weekEnd + 1;
    for (let i = trueWeekStart; i <= trueWeekEnd; i++) {
      const container = (weeks[i] = weeks[i] || {
        count: i,
        messages: [],
        tasks: []
      });
      container.tasks.push(t);
    }
  });

  weeks[0] = {
    count: 0,
    anchor: {
      at: instance.data.anchor?.at || instance.data.startsAt,
      label: instance.data.anchor?.label || ''
    },
    tasks: [],
    messages: []
  };

  return sortBy(Object.values(weeks), (w) => w.count);
};

const SubText = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w700
}));

type _WeekStatus = 'COMPLETE' | 'LATE' | null;
const getWeekStatus = (d: InstanceWeek): _WeekStatus => {
  if (d.tasks.length) {
    if (d.tasks.every((tsk) => tsk.status === TaskStatus.COMPLETE)) {
      return 'COMPLETE';
    }
    if (d.tasks.some((tsk) => tsk.status === TaskStatus.LATE)) {
      return 'LATE';
    }
  }
  return null;
};
const useWeekStatus = (d: InstanceWeek): _WeekStatus => {
  return useMemo(() => getWeekStatus(d), [d]);
};

const WeekStatus = ({ status }: { status: _WeekStatus }) => {
  const { t } = useUnsafeTranslation();

  if (!status) {
    return null;
  }
  const color = status === 'LATE' ? 'red' : 'green';
  const label = status === 'LATE' ? t('Overdue') : t('Complete');
  return (
    <ColoredChip
      color={color}
      size="small"
      width={'auto'}
      variant="rounded"
      mode="light"
    >
      {label}
    </ColoredChip>
  );
};

const BubbleGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '20px 1fr',
  gridColumnGap: p.theme.spacing(2),
  alignItems: 'center',
  width: '100%',
  position: 'relative',
  zIndex: 0
}));

const BUBBLE_MAX_SIZE = 20;

const BubbleContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: BUBBLE_MAX_SIZE,
  height: BUBBLE_MAX_SIZE
}));

const BubbleLineTop = styled('div')<{
  mode: 'previous' | 'current' | 'future';
}>((p) => ({
  position: 'absolute',
  left: BUBBLE_MAX_SIZE / 2 - 1,
  borderLeft: p.theme.custom.border.standard,
  borderWidth: 2,
  top: 0 - p.theme.spacing(1),
  height: `calc(50% + ${p.theme.spacing(1)}px)`,
  zIndex: 0,

  borderColor:
    p.mode === 'future'
      ? p.theme.custom.colors.gray.w400
      : p.theme.custom.colors.primary.main
}));

const BubbleLineBottom = styled('div')<{
  mode: 'previous' | 'current' | 'future';
}>((p) => ({
  position: 'absolute',
  left: BUBBLE_MAX_SIZE / 2 - 1,
  borderLeft: p.theme.custom.border.standard,
  borderWidth: 2,
  top: '50%',
  height: `calc(50% + ${p.theme.spacing(1)}px)`,
  zIndex: 0,

  borderColor:
    p.mode === 'future'
      ? p.theme.custom.colors.gray.w400
      : p.theme.custom.colors.primary.main
}));

const Bubble = styled('div')<{
  mode: 'previous' | 'current' | 'future';
  warning?: boolean;
}>((p) => {
  const size = p.mode === 'current' ? 20 : p.mode === 'previous' ? 16 : 12;
  return {
    borderRadius: '50%',
    height: size,
    width: size,
    borderStyle: 'solid',
    borderWidth: 2,
    borderColor: p.warning
      ? p.theme.custom.colors.red.w600
      : p.mode === 'future'
      ? p.theme.custom.colors.gray.w400
      : p.theme.custom.colors.primary.main,
    backgroundColor: p.warning
      ? p.theme.custom.colors.red.w200
      : p.mode === 'previous'
      ? p.theme.custom.colors.primary.main
      : p.mode === 'current'
      ? p.theme.custom.colors.primary.light
      : NAV_BG_COLOR,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '> *': {
      color: p.warning
        ? p.theme.custom.colors.red.w600
        : p.theme.custom.colors.primary.contrastText,
      strokeWidth: 4,
      visibility: p.mode === 'previous' ? 'visible' : 'hidden'
    },
    zIndex: 1
  };
});

const WeekItemCard = ({
  weekGroup,
  isSelected,
  chevronDirection = 'right',
  onClick = () => null, // forcing a cursor: pointer
  noRoundBorders,
  noChevron,
  hoverEffect,
  trackFn,
  mode,
  currentWeek
}: {
  weekGroup: InstanceWeek;
  isSelected: boolean;
  chevronDirection?: 'right' | 'down';
  onClick?: () => void;
  noRoundBorders?: boolean;
  noChevron?: boolean;
  hoverEffect?: boolean;
  trackFn?: (ev: ProductAnalyticsEvent) => void;
  mode: 'previous' | 'current' | 'future' | 'dropdown';
  currentWeek: number;
}) => {
  const { t } = useUnsafeTranslation();
  const ChevronComponent =
    chevronDirection === 'right' ? ChevronRight : ChevronDown;
  const weekStatus = useWeekStatus(weekGroup);
  return (
    <EmployeeAppCard
      onClick={
        onClick || trackFn
          ? () => {
              trackFn?.({
                eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
                name: 'Timeline Navigation Clicked',
                Explanation:
                  'User clicked on a week in the timeline navigation',
                Week: weekGroup.count,
                'Task Count': weekGroup.tasks.length,
                'Message Count': weekGroup.messages.length,
                'Week Status':
                  weekStatus === 'COMPLETE'
                    ? 'Completed'
                    : weekStatus === 'LATE'
                    ? 'Overdue'
                    : 'not set',

                'Week Type': isSelected
                  ? 'Selected'
                  : currentWeek < weekGroup.count
                  ? 'Future'
                  : 'Past'
              });
              onClick?.();
            }
          : undefined
      }
      noRoundBorders={noRoundBorders}
      highlight={isSelected && mode === 'dropdown'}
      hoverEffect={hoverEffect}
    >
      <FlexContainerVertical spacing={0.5}>
        <Typography variant="h6">
          {t('Week {{ week }}', { week: weekGroup.count })}
        </Typography>
        <FlexContainer fullWidth justifyContent={'space-between'}>
          <SubText>
            {compact([
              weekGroup.tasks.length
                ? t('taskWithCount', { count: weekGroup.tasks.length })
                : null,
              weekGroup.messages.length
                ? t('messagesWithCount', { count: weekGroup.messages.length })
                : null
            ]).join(' • ')}
          </SubText>
          <FlexContainer>
            <WeekStatus status={weekStatus} />
            {!noChevron && (
              <IconButton>
                <ChevronComponent
                  size={24}
                  // Making it white is making it invisible, but still take up its space
                  color={isSelected ? 'transparent' : undefined}
                />
              </IconButton>
            )}
          </FlexContainer>
        </FlexContainer>
      </FlexContainerVertical>
    </EmployeeAppCard>
  );
};

const WeekItem = ({
  weekGroup,
  isSelected,
  mode,
  first,
  last,
  firstCurrent,
  trackFn,
  currentWeek
}: {
  weekGroup: InstanceWeek;
  isSelected: boolean;
  mode: 'previous' | 'current' | 'future';
  first?: boolean;
  last?: boolean;
  firstCurrent?: boolean;
  trackFn?: (ev: ProductAnalyticsEvent) => void;
  currentWeek: number;
}) => {
  const { t } = useUnsafeTranslation();
  const weekStatus = useWeekStatus(weekGroup);
  const bottomMode = mode === 'current' && firstCurrent ? 'future' : mode;
  if (weekGroup.anchor) {
    return (
      <BubbleGrid>
        <BubbleContainer>
          <Bubble mode={mode}>
            <Check size={10} />
          </Bubble>
        </BubbleContainer>
        <WeekNavText>
          <LightMdToJsx>
            {t('{{ label }} **{{ date }}**', {
              label: capitalize(
                weekGroup.anchor.label || t('Experience start')
              ),
              date: weekGroup.anchor.at ? toMoment(weekGroup.anchor.at).format('MMM Do') : 'N/A'
            })}
          </LightMdToJsx>
        </WeekNavText>
        {!first && <BubbleLineTop mode={mode} />}
        {!last && <BubbleLineBottom mode={bottomMode} />}
      </BubbleGrid>
    );
  }
  return (
    <BubbleGrid>
      <BubbleContainer>
        <Bubble mode={mode} warning={weekStatus === 'LATE'}>
          {weekStatus === 'LATE' ? (
            <ExclamationMarkIcon size={10} />
          ) : (
            <Check size={10} />
          )}
        </Bubble>
      </BubbleContainer>
      <WeekItemCard
        weekGroup={weekGroup}
        isSelected={isSelected}
        chevronDirection="right"
        hoverEffect
        trackFn={trackFn}
        mode={mode}
        currentWeek={currentWeek}
      />
      {!first && <BubbleLineTop mode={mode} />}
      {!last && <BubbleLineBottom mode={bottomMode} />}
    </BubbleGrid>
  );
};

const Container = styled('div')((p) => ({
  backgroundColor: NAV_BG_COLOR,
  paddingTop: p.theme.spacing(4),
  paddingBottom: p.theme.spacing(4),
  paddingLeft: p.theme.spacing(2),
  paddingRight: p.theme.spacing(3),
  height: '100%'
}));

export const WeekSelectorBar = ({
  instance,
  weekGroups,
  selectedWeek,
  setSelectedWeekToUrl,
  n,
  trackFn,
  currentWeek
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  weekGroups: InstanceWeek[];
  selectedWeek: number;
  setSelectedWeekToUrl: (nextWeek: number) => string;
  n: moment.Moment;
  trackFn: (ev: ProductAnalyticsEvent) => void;
  currentWeek: number;
}) => {
  const { t } = useUnsafeTranslation();
  const [previousWeeks, currentWeeks] = useMemo(() => {
    const startingWeek = getStartingWeek(instance, weekGroups, n);
    return partition(weekGroups, (w) =>
      startingWeek === 1
        ? w.count < startingWeek && w.count !== 0
        : w.count < startingWeek
    );
  }, [instance, weekGroups, n]);
  const [expandPreviousWeeks, setExpandPreviousWeeks] = useState(
    !!previousWeeks.find((w) => w.count === selectedWeek)
  );

  const { experienceEnd } = instance.data;

  const showPreviousWeeksButton = previousWeeks.length > 1;

  const anyPreviousLate = useMemo(() => {
    return previousWeeks.some((x) => getWeekStatus(x) === 'LATE');
  }, [previousWeeks]);

  return (
    <Container>
      <FlexContainerVertical spacing={2}>
        <>
          {showPreviousWeeksButton && (
            <BubbleGrid>
              <BubbleContainer>
                <Bubble mode="previous" warning={anyPreviousLate}>
                  {anyPreviousLate ? (
                    <ExclamationMarkIcon size={10} />
                  ) : (
                    <Check size={10} />
                  )}
                </Bubble>
              </BubbleContainer>
              <EmployeeAppCard
                onClick={() => {
                  trackFn?.({
                    eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
                    name: 'Timeline Navigation Clicked',
                    Explanation:
                      'User clicked on a week in the timeline navigation',
                    'Task Count': 0,
                    'Message Count': 0,
                    'Week Status': 'not set',
                    'Week Type': 'Previous weeks'
                  });
                  setExpandPreviousWeeks((x) => !x);
                }}
                role="button"
              >
                <FlexContainer fullWidth justifyContent="space-between">
                  <Typography variant="h6">
                    {t('{{ count }} previous weeks', {
                      count: previousWeeks.filter((w) => !w.anchor).length
                    })}
                  </Typography>
                  <IconButton>
                    {expandPreviousWeeks ? (
                      <ChevronUp size={24} />
                    ) : (
                      <ChevronDown size={24} />
                    )}
                  </IconButton>
                </FlexContainer>
              </EmployeeAppCard>
              <BubbleLineBottom mode="previous" />
            </BubbleGrid>
          )}
          {(expandPreviousWeeks || previousWeeks.length < 2) &&
            previousWeeks.map((w, i) => (
              <Link
                to={setSelectedWeekToUrl(w.count)}
                style={{ width: '100%' }}
              >
                <WeekItem
                  key={w.count}
                  weekGroup={w}
                  isSelected={w.count === selectedWeek}
                  mode="previous"
                  first={!showPreviousWeeksButton && i === 0}
                  last={!currentWeeks.length && i === previousWeeks.length - 1}
                  trackFn={trackFn}
                  currentWeek={currentWeek}
                />
              </Link>
            ))}

          {currentWeeks.map((w, i) => {
            const trueI = currentWeeks.filter((w) => !w.anchor).indexOf(w);
            return (
              <Link
                to={setSelectedWeekToUrl(w.count)}
                style={{ width: '100%' }}
              >
                <WeekItem
                  key={w.count}
                  weekGroup={w}
                  isSelected={w.count === selectedWeek}
                  mode={
                    i === 0 && !!w.anchor
                      ? 'previous'
                      : trueI === 0
                      ? 'current'
                      : 'future'
                  }
                  first={i + previousWeeks.length === 0}
                  last={false /* there's always the ending item */}
                  firstCurrent={trueI === 0}
                  trackFn={trackFn}
                  currentWeek={currentWeek}
                />
              </Link>
            );
          })}
        </>
        <BubbleGrid>
          <BubbleContainer>
            <Bubble mode={'future'}>
              <Check size={10} />
            </Bubble>
          </BubbleContainer>
          <WeekNavText>
            <LightMdToJsx>
              {t('Experience ends **{{ experienceEnd }}**', {
                experienceEnd: experienceEnd?.at ? toMoment(experienceEnd.at).format('MMM Do') : 'N/A'
              })}
            </LightMdToJsx>
          </WeekNavText>
          <BubbleLineTop mode={'future'} />
        </BubbleGrid>
        <Spacer height={8} bgColor={() => NAV_BG_COLOR} />
      </FlexContainerVertical>
    </Container>
  );
};

export const WeekSelectorDropdown = ({
  instance,
  weekGroups,
  selectedWeek,
  setSelectedWeekToUrl,
  n,
  trackFn,
  currentWeek
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  weekGroups: InstanceWeek[];
  selectedWeek: number;
  setSelectedWeekToUrl: (nextWeek: number) => string;
  n: moment.Moment;
  trackFn: (ev: ProductAnalyticsEvent) => void;
  currentWeek: number;
}) => {
  const { t } = useUnsafeTranslation();
  const { experienceEnd } = instance.data;

  const selected =
    weekGroups.find((w) => w.count === selectedWeek) ||
    weekGroups[weekGroups.length - 1];
  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  return (
    <>
      <div ref={ref}>
        <WeekItemCard
          weekGroup={selected}
          chevronDirection="down"
          isSelected={false}
          onClick={() => setOpen(true)}
          mode="dropdown"
          currentWeek={currentWeek}
        />
        <Popover
          anchorEl={ref.current}
          open={open}
          onClose={() => setOpen(false)}
          classes={{
            paper: css({ borderRadius: `${theme.spacing(2)}px !important` })
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            style: {
              width: `calc(100% - ${theme.spacing(8)}px)`
            }
          }}
        >
          <FlexContainerVertical fullWidth spacing={0}>
            {weekGroups.map((w) =>
              w.anchor ? (
                <EmployeeAppCard key={w.count} noRoundBorders>
                  <WeekNavText>
                    <LightMdToJsx>
                      {t('{{ label }} **{{ date }}**', {
                        label: capitalize(
                          w.anchor.label || t('Experience start')
                        ),
                        date: w.anchor.at ? toMoment(w.anchor.at).format('MMM Do') : 'N/A'
                      })}
                    </LightMdToJsx>
                  </WeekNavText>
                </EmployeeAppCard>
              ) : (
                <Link
                  to={setSelectedWeekToUrl(w.count)}
                  style={{ width: '100%' }}
                  key={w.count}
                >
                  <WeekItemCard
                    weekGroup={w}
                    isSelected={w === selected}
                    onClick={() => setOpen(false)}
                    noRoundBorders
                    noChevron
                    hoverEffect
                    trackFn={trackFn}
                    mode="dropdown"
                    currentWeek={currentWeek}
                  />
                </Link>
              )
            )}
            <EmployeeAppCard noRoundBorders>
              <WeekNavText>
                <LightMdToJsx>
                  {t('Experience ends **{{ experienceEnd }}**', {
                    experienceEnd: experienceEnd?.at ? toMoment(experienceEnd.at).format('MMM Do') : 'N/A'
                  })}
                </LightMdToJsx>
              </WeekNavText>
            </EmployeeAppCard>
          </FlexContainerVertical>
        </Popover>
      </div>
    </>
  );
};

export const WeekSelector = (props: {
  instance: Doc<IExperienceDenormalizedV2>;
  weekGroups: InstanceWeek[];
  selectedWeek: number;
  currentWeek: number;
  setSelectedWeekToUrl: (nextWeek: number) => string;
  n: moment.Moment;
  trackFn: (ev: ProductAnalyticsEvent) => void;
}) => {
  return (
    <WithBreakpoints
      lgMd={<WeekSelectorBar {...props} />}
      smXs={<WeekSelectorDropdown {...props} />}
    />
  );
};
